import React, { useState } from 'react';
import { Share2, Copy, Check, MessageCircle, Send, Facebook, Twitter, Link2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { copyToClipboard } from '../utils/clipboard';
import toast from 'react-hot-toast';

const ShareButton = ({ 
  type = 'product', // 'product', 'service', 'activation', 'shop', 'page'
  itemId = '',
  itemName = '',
  className = '',
  variant = 'icon', // 'icon', 'button', 'full'
  showLabel = false
}) => {
  const { user } = useAuth();
  const [showMenu, setShowMenu] = useState(false);
  const [copied, setCopied] = useState(false);

  // Build referral link based on type
  const buildReferralLink = () => {
    const baseUrl = window.location.origin;
    const referralId = user?.username || user?.id || '';
    
    let path = '';
    switch (type) {
      case 'product':
        // Use share endpoint for OG tags and image preview
        path = `/api/share/product/${itemId}`;
        break;
      case 'service':
        path = `/api/share/product/${itemId}`;
        break;
      case 'listing':
        path = `/api/share/listing/${itemId}`;
        break;
      case 'auction':
        path = `/api/share/auction/${itemId}`;
        break;
      case 'dvc':
        path = `/api/share/dvc/${itemId}`;
        break;
      case 'biodata':
        path = `/api/share/biodata/${itemId}`;
        break;
      case 'activation':
        path = `/register?ref=${referralId}&activation=${itemId}`;
        break;
      case 'shop':
        path = `/shop/${itemId}?ref=${referralId}`;
        break;
      case 'page':
        path = `/register?ref=${referralId}`;
        break;
      default:
        path = `/register?ref=${referralId}`;
    }
    
    return `${baseUrl}${path}`;
  };

  const shareLink = buildReferralLink();
  const shareText = itemName 
    ? `Check out ${itemName} on ANANT WEB PORTAL!` 
    : 'Join ANANT WEB PORTAL today!';

  const handleCopyLink = async () => {
    const success = await copyToClipboard(shareLink);
    if (success) {
      setCopied(true);
      toast.success('Link copied with your referral ID!');
      setTimeout(() => setCopied(false), 2000);
    }
    setShowMenu(false);
  };

  const handleShareWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(`${shareText}\n\n${shareLink}`)}`;
    window.open(url, '_blank');
    setShowMenu(false);
  };

  const handleShareTelegram = () => {
    const url = `https://t.me/share/url?url=${encodeURIComponent(shareLink)}&text=${encodeURIComponent(shareText)}`;
    window.open(url, '_blank');
    setShowMenu(false);
  };

  const handleShareFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareLink)}`;
    window.open(url, '_blank');
    setShowMenu(false);
  };

  const handleShareTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareLink)}`;
    window.open(url, '_blank');
    setShowMenu(false);
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: itemName || 'ANANT WEB PORTAL',
          text: shareText,
          url: shareLink
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          handleCopyLink();
        }
      }
    } else {
      handleCopyLink();
    }
    setShowMenu(false);
  };

  if (variant === 'icon') {
    return (
      <div className={`relative ${className}`}>
        <button
          onClick={() => setShowMenu(!showMenu)}
          className="p-2 rounded-lg bg-primary/10 hover:bg-primary/20 text-primary transition-colors"
          title="Share with referral link"
          data-testid={`share-btn-${type}-${itemId}`}
        >
          <Share2 className="w-4 h-4" />
        </button>

        {showMenu && (
          <>
            <div 
              className="fixed inset-0 z-40" 
              onClick={() => setShowMenu(false)}
            />
            <div className="absolute right-0 top-full mt-2 z-50 w-48 bg-card border border-white/10 rounded-xl shadow-xl overflow-hidden">
              <div className="p-2 border-b border-white/10">
                <p className="text-xs text-muted-foreground">Share with your referral ID</p>
              </div>
              <div className="p-1">
                <button
                  onClick={handleCopyLink}
                  className="w-full flex items-center gap-3 px-3 py-2 hover:bg-white/5 rounded-lg transition-colors"
                >
                  {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  <span className="text-sm">Copy Link</span>
                </button>
                <button
                  onClick={handleShareWhatsApp}
                  className="w-full flex items-center gap-3 px-3 py-2 hover:bg-white/5 rounded-lg transition-colors"
                >
                  <MessageCircle className="w-4 h-4 text-green-500" />
                  <span className="text-sm">WhatsApp</span>
                </button>
                <button
                  onClick={handleShareTelegram}
                  className="w-full flex items-center gap-3 px-3 py-2 hover:bg-white/5 rounded-lg transition-colors"
                >
                  <Send className="w-4 h-4 text-blue-400" />
                  <span className="text-sm">Telegram</span>
                </button>
                <button
                  onClick={handleShareFacebook}
                  className="w-full flex items-center gap-3 px-3 py-2 hover:bg-white/5 rounded-lg transition-colors"
                >
                  <Facebook className="w-4 h-4 text-blue-500" />
                  <span className="text-sm">Facebook</span>
                </button>
                <button
                  onClick={handleShareTwitter}
                  className="w-full flex items-center gap-3 px-3 py-2 hover:bg-white/5 rounded-lg transition-colors"
                >
                  <Twitter className="w-4 h-4 text-sky-400" />
                  <span className="text-sm">Twitter</span>
                </button>
                {navigator.share && (
                  <button
                    onClick={handleNativeShare}
                    className="w-full flex items-center gap-3 px-3 py-2 hover:bg-white/5 rounded-lg transition-colors"
                  >
                    <Share2 className="w-4 h-4" />
                    <span className="text-sm">More Options</span>
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  if (variant === 'button') {
    return (
      <button
        onClick={handleNativeShare}
        className={`flex items-center gap-2 px-4 py-2 bg-primary/10 hover:bg-primary/20 text-primary rounded-lg transition-colors ${className}`}
        data-testid={`share-btn-${type}-${itemId}`}
      >
        <Share2 className="w-4 h-4" />
        {showLabel && <span>Share</span>}
      </button>
    );
  }

  // Full variant with all options visible
  return (
    <div className={`space-y-3 ${className}`}>
      <div className="flex items-center gap-2">
        <Link2 className="w-4 h-4 text-primary" />
        <span className="text-sm font-medium">Share with Referral Link</span>
      </div>
      
      <div className="flex items-center gap-2 p-2 bg-white/5 rounded-lg">
        <input
          type="text"
          value={shareLink}
          readOnly
          className="flex-1 bg-transparent text-sm text-muted-foreground truncate"
        />
        <button
          onClick={handleCopyLink}
          className="p-2 bg-primary/20 rounded-lg hover:bg-primary/30 transition-colors"
        >
          {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-primary" />}
        </button>
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={handleShareWhatsApp}
          className="flex-1 flex items-center justify-center gap-2 p-3 bg-green-500/20 hover:bg-green-500/30 rounded-lg transition-colors"
        >
          <MessageCircle className="w-5 h-5 text-green-500" />
          <span className="text-sm">WhatsApp</span>
        </button>
        <button
          onClick={handleShareTelegram}
          className="flex-1 flex items-center justify-center gap-2 p-3 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg transition-colors"
        >
          <Send className="w-5 h-5 text-blue-400" />
          <span className="text-sm">Telegram</span>
        </button>
      </div>
    </div>
  );
};

export default ShareButton;
